import React from 'react';
import { useRouter } from 'next/router';
import { routes } from '../../../config/routes';

interface ViewTransition {
    ready: Promise<void>;
    updateCallbackDone: Promise<void>;
    finished: Promise<void>;
    skipTransition: () => void;
}

type TransitionDocument = Document & {
    startViewTransition?: (update: () => Promise<void>) => ViewTransition;
};

const publicPaths = new Set([
    ...Object.values(routes.web).map((path) => path.split(/[?#]/)[0]),
    '/compound-interest',
    '/stan-se-partnerem',
]);

const isPublicPath = (path: string) => publicPaths.has(path);

const pathname = (url: string) => new URL(url, window.location.origin).pathname;

/** Animation only: Next still owns navigation, server rendering, metadata and history. */
export const PageTransitions: React.FC = () => {
    const router = useRouter();

    React.useEffect(() => {
        const documentWithTransitions = document as TransitionDocument;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        let transition: ViewTransition | undefined;
        let timer = 0;
        let frame = 0;
        let managedNavigations = 0;
        let savedScrollBehavior = '';
        let navigationId = 0;
        let previousPath = pathname(router.asPath);
        let previousPill: DOMRect | undefined;
        let animateNextPage = false;
        let animations: Animation[] = [];

        const stopTransition = () => {
            window.clearTimeout(timer);
            transition?.skipTransition();
            transition = undefined;
        };
        const isPageChange = (url: string) => {
            const next = pathname(url);
            return next !== previousPath && isPublicPath(previousPath) && isPublicPath(next);
        };
        const onStart = (url: string) => {
            animateNextPage = isPageChange(url) && !reducedMotion.matches && managedNavigations === 0;
            previousPill = document.querySelector('[data-web-nav-pill]')?.getBoundingClientRect();
        };
        const onComplete = (url: string) => {
            previousPath = pathname(url);
            if (!animateNextPage) return;
            animateNextPage = false;
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => {
                animations.forEach((animation) => animation.cancel());
                animations = Array.from(document.querySelectorAll('[data-web-transition-content]')).map((element) =>
                    element.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' }),
                );
                const pill = document.querySelector('[data-web-nav-pill]');
                if (pill && previousPill?.width) {
                    const next = pill.getBoundingClientRect();
                    if (next.width) {
                        animations.push(
                            pill.animate(
                                [
                                    {
                                        transform: `translateX(${previousPill.left - next.left}px) scaleX(${
                                            previousPill.width / next.width
                                        })`,
                                        transformOrigin: 'left center',
                                    },
                                    { transform: 'none', transformOrigin: 'left center' },
                                ],
                                { duration: 340, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
                            ),
                        );
                    }
                }
            });
        };
        const onError = () => {
            animateNextPage = false;
        };
        const onClick = (event: MouseEvent) => {
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey ||
                reducedMotion.matches ||
                !documentWithTransitions.startViewTransition
            )
                return;
            const link = (event.target as Element | null)?.closest<HTMLAnchorElement>('a[href]');
            if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
            const url = new URL(link.href);
            // Leave anchors, dialog/query actions, external links and the signed-in app to their own handlers.
            if (url.origin !== window.location.origin || url.search || url.hash || !isPageChange(url.href)) return;
            event.preventDefault();
            stopTransition();
            navigationId += 1;
            const id = navigationId;
            const current = documentWithTransitions.startViewTransition(async () => {
                if (id !== navigationId) return;
                if (managedNavigations === 0) savedScrollBehavior = document.documentElement.style.scrollBehavior;
                managedNavigations += 1;
                document.documentElement.style.scrollBehavior = 'auto';
                try {
                    await router.push(`${url.pathname}${url.search}${url.hash}`);
                    // Store hydration can briefly unmount the layout after routeChangeComplete.
                    await new Promise<void>((resolve) => {
                        const deadline = Date.now() + 1000;
                        const waitForLayout = () => {
                            if (document.querySelector('[data-web-transition-content]') || Date.now() >= deadline)
                                resolve();
                            else window.setTimeout(waitForLayout, 16);
                        };
                        window.setTimeout(waitForLayout, 0);
                    });
                } finally {
                    managedNavigations -= 1;
                    if (managedNavigations === 0) document.documentElement.style.scrollBehavior = savedScrollBehavior;
                }
            });
            transition = current;
            // Slow or cancelled navigation must never leave a screenshot over the interactive page.
            timer = window.setTimeout(() => current.skipTransition(), 1800);
            current.updateCallbackDone.catch(() => undefined);
            current.ready.catch(() => undefined);
            current.finished
                .catch(() => undefined)
                .finally(() => {
                    if (transition === current) {
                        window.clearTimeout(timer);
                        transition = undefined;
                    }
                });
        };

        document.addEventListener('click', onClick, true);
        router.events.on('routeChangeStart', onStart);
        router.events.on('routeChangeComplete', onComplete);
        router.events.on('routeChangeError', onError);
        return () => {
            navigationId += 1;
            stopTransition();
            window.cancelAnimationFrame(frame);
            animations.forEach((animation) => animation.cancel());
            document.removeEventListener('click', onClick, true);
            router.events.off('routeChangeStart', onStart);
            router.events.off('routeChangeComplete', onComplete);
            router.events.off('routeChangeError', onError);
        };
    }, [router.events]);

    return null;
};
